// frontend/src/pages/SearchResults.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { flightsAPI } from '../services/flights';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import FlightCard from '../components/flight/FlightCard';
import FlightFilters from '../components/flight/FlightFilters';

const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { searchParams: contextSearchParams, updateSearchResults, selectFlight } = useBooking();
  
  const [flights, setFlights] = useState([]);
  const [filteredFlights, setFilteredFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    maxDuration: '',
    maxStops: '',
    sortBy: 'price'
  });

  useEffect(() => {
    searchFlights();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [flights, filters]);

  const searchFlights = async () => {
    setLoading(true);
    setError('');

    try {
      // Use URL params if available, otherwise use context
      const params = {
        origin: searchParams.get('origin') || contextSearchParams.origin,
        destination: searchParams.get('destination') || contextSearchParams.destination,
        departureDate: searchParams.get('departureDate') || contextSearchParams.departureDate,
        returnDate: searchParams.get('returnDate') || contextSearchParams.returnDate,
        passengers: parseInt(searchParams.get('passengers')) || contextSearchParams.passengers,
        classType: searchParams.get('classType') || contextSearchParams.classType
      };

      if (!params.origin || !params.destination || !params.departureDate) {
        navigate('/');
        return;
      }

      const results = await flightsAPI.searchFlights(params);
      setFlights(results);
      updateSearchResults(results);
    } catch (err) {
      setError('Failed to search flights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...flights];

    if (filters.minPrice) {
      filtered = filtered.filter(f => f.base_price >= parseFloat(filters.minPrice));
    }

    if (filters.maxPrice) {
      filtered = filtered.filter(f => f.base_price <= parseFloat(filters.maxPrice));
    }

    if (filters.maxDuration) {
      filtered = filtered.filter(f => f.duration <= parseInt(filters.maxDuration));
    }

    if (filters.maxStops) {
      filtered = filtered.filter(f => f.stops <= parseInt(filters.maxStops));
    }

    // Sort flights
    switch (filters.sortBy) {
      case 'price':
        filtered.sort((a, b) => a.base_price - b.base_price);
        break;
      case 'duration':
        filtered.sort((a, b) => a.duration - b.duration);
        break;
      case 'departure_time':
        filtered.sort((a, b) => new Date(a.departure_time) - new Date(b.departure_time));
        break;
      default:
        break;
    }

    setFilteredFlights(filtered);
  };

  const handleFlightSelect = (flight) => {
    selectFlight(flight);
    navigate(`/booking/${flight.id}`);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  if (loading) {
    return <Loading message="Searching flights..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Flight Search Results
          </h1>
          <p className="text-gray-600">
            Found {filteredFlights.length} flights from {contextSearchParams.origin} to {contextSearchParams.destination}
          </p>
        </div>

        {error && <ErrorMessage message={error} onRetry={searchFlights} />}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <div className="lg:w-1/4">
            <FlightFilters
              filters={filters}
              onFiltersChange={handleFilterChange}
              flights={flights}
            />
          </div>

          {/* Results */}
          <div className="lg:w-3/4">
            {filteredFlights.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">✈️</div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">
                  No flights found
                </h2>
                <p className="text-gray-500 mb-4">
                  Try adjusting your filters or search criteria
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="btn btn-primary"
                >
                  Start New Search
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFlights.map(flight => (
                  <FlightCard
                    key={flight.id}
                    flight={flight}
                    onSelect={() => handleFlightSelect(flight)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;





